import { useRef, useCallback, useState, useEffect } from 'react';
import MapGL, { type MapRef, NavigationControl } from 'react-map-gl';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';
import type { MapViewState } from '@/types';
import { MAPBOX_TOKEN, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '@/constants';
import { useSearch } from '@/context/SearchContext';
import { useStation } from '@/context/StationContext';
import { useFilters } from '@/context/FiltersContext';
import { useStations } from '@/hooks/useStations';
import { StationMarker } from './StationMarker';
import { UserLocationMarker } from './UserLocationMarker';
import { RadiusCircle } from './RadiusCircle';

import 'mapbox-gl/dist/mapbox-gl.css';

interface MapProps {
  className?: string;
  style?: React.CSSProperties;
}

// Helper to calculate appropriate zoom for radius
function getZoomForRadius(radiusMiles: number): number {
  // Approximate zoom levels for different radii
  if (radiusMiles <= 5) return 12;
  if (radiusMiles <= 10) return 11;
  if (radiusMiles <= 25) return 10;
  return 9;
}

export function Map({ className, style }: MapProps) {
  const mapRef = useRef<MapRef>(null);
  const { location, isExpanded, setLocation } = useSearch();
  const { radius } = useFilters();
  const { selectStation, selectedStation } = useStation();

  // Only fetch stations when we have a location
  const { stations, isLoading, error } = useStations({
    lat: location?.latitude ?? 0,
    lng: location?.longitude ?? 0,
    radius,
    enabled: !!location,
  });

  const [viewState, setViewState] = useState<MapViewState>({
    longitude: DEFAULT_MAP_CENTER.lng,
    latitude: DEFAULT_MAP_CENTER.lat,
    zoom: DEFAULT_MAP_ZOOM,
  });

  // Track whether the current map movement is programmatic (flyTo) or user-initiated
  const programmaticMoveRef = useRef(false);
  const [showReSearchButton, setShowReSearchButton] = useState(false);

  // Fly to location when search location changes
  useEffect(() => {
    if (location && mapRef.current) {
      programmaticMoveRef.current = true;
      setShowReSearchButton(false);
      mapRef.current.flyTo({
        center: [location.longitude, location.latitude],
        zoom: getZoomForRadius(radius),
        duration: 1500,
        essential: true,
      });
    }
  }, [location, radius]);

  // Fly to station when selected
  useEffect(() => {
    if (selectedStation && mapRef.current) {
      programmaticMoveRef.current = true;
      setShowReSearchButton(false);
      mapRef.current.flyTo({
        center: [selectedStation.longitude, selectedStation.latitude],
        zoom: 14,
        duration: 800,
        essential: true,
      });
    }
  }, [selectedStation]);

  const handleMove = useCallback((evt: { viewState: MapViewState }) => {
    setViewState(evt.viewState);
  }, []);

  // After the map finishes moving, decide whether to show the re-search button.
  // Programmatic moves (flyTo) clear the flag; user-initiated moves show the button.
  const handleMoveEnd = useCallback(() => {
    if (programmaticMoveRef.current) {
      programmaticMoveRef.current = false;
      return;
    }
    if (location) {
      setShowReSearchButton(true);
    }
  }, [location]);

  const handleReSearch = useCallback(() => {
    setLocation(
      { latitude: viewState.latitude, longitude: viewState.longitude },
      null,
    );
    setShowReSearchButton(false);
  }, [setLocation, viewState.latitude, viewState.longitude]);

  // Determine if map should be dimmed (no location yet, panel is expanded)
  const isDimmed = !location && isExpanded;

  return (
    <div className={clsx('relative', className)} style={style}>
      {/* Map */}
      <MapGL
        ref={mapRef}
        {...viewState}
        onMove={handleMove}
        onMoveEnd={handleMoveEnd}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
      >
        {/* Navigation controls */}
        <NavigationControl position="bottom-right" showCompass={false} />

        {/* Radius circle */}
        {location && (
          <RadiusCircle
            latitude={location.latitude}
            longitude={location.longitude}
            radiusMiles={radius}
          />
        )}

        {/* User location marker */}
        {location && (
          <UserLocationMarker
            latitude={location.latitude}
            longitude={location.longitude}
          />
        )}

        {/* Station markers */}
        {stations.map((station) => (
          <StationMarker
            key={station.id}
            station={station}
            allStations={stations}
            onClick={() => selectStation(station)}
            isSelected={selectedStation?.id === station.id}
          />
        ))}
      </MapGL>

      {/* Dimmed overlay when no location */}
      <motion.div
        initial={false}
        animate={{ opacity: isDimmed ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={clsx(
          'absolute inset-0 pointer-events-none',
          'bg-white/40 backdrop-blur-[2px]'
        )}
        style={{ display: isDimmed ? 'block' : 'none' }}
      />

      {/* Re-search button — shown after the user pans/zooms away from the search centre */}
      {showReSearchButton && !isLoading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <button
            type="button"
            onClick={handleReSearch}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-full shadow-lg',
              'bg-white border border-brand-500 text-brand-700 text-sm font-medium',
              'hover:bg-brand-50 active:bg-brand-100',
              'focus:outline-none focus:ring-2 focus:ring-brand-300',
              'transition-colors duration-150'
            )}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
            Search this area
          </button>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && location && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <div className="bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-600">Loading stations...</span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm shadow-lg">
            Failed to load stations. Please try again.
          </div>
        </div>
      )}
    </div>
  );
}
